import _ from 'lodash';
import {
  couponDiscountTypeMap,
  PROMOTION_TYPE,
  couponFetchStatus,
  couponFormModeMap,
} from '@/views/config/coupon/helper/constant';
import { isEmptyVal } from '@/utils';
import { couponParamsGeneratorInstance } from '@/views/config/coupon/helper/generate-params';
import { validateCoupon } from '@/apis/coupon';
import { logger } from '@/utils/log';
import { softBundlePopUpMsgWithCoupon } from '@/views/config/soft-bundle/pop-message';
import { couponStrategyFactory } from '../factory';

/**
 * Convert verify coupon API result to coupon state
 * Uses strategy factory pattern for type-specific conversion
 * @param {Object} couponResult - Coupon result from API
 * @param {Object} options - Options object
 * @param {string} options.couponCode - Coupon code
 * @param {boolean} options.allowChange - Whether coupon can be changed
 * @returns {Object} Coupon state object
 */
export const convertVerifyCouponResult2State = (couponResult = {}, options = {}) => {
  return couponStrategyFactory.convertVerifyResultToState(couponResult, options);
};

/**
 * Convert campaign coupon result to coupon state
 * Uses strategy factory pattern for type-specific conversion
 * @param {Object} marketingCoupon - Marketing coupon data from backend
 * @returns {PreloadCouponBean} Preload coupon state
 */
export const convertCampaignCouponResult2State = (marketingCoupon = {}) => {
  return couponStrategyFactory.convertCampaignResultToState(marketingCoupon);
};

/**
 * Convert subscription result to coupon state
 * @param {Array} subscriptions - Array of subscription data
 * @returns {Array} Array of coupon state objects with expireDate and discountType
 */
export const convertSubscriptionResult2State = (subscriptions = []) => {
  return subscriptions
    .filter((subscription) => subscription.discount)
    .map((subscription) => ({
      ...subscription,
      expireDate: subscription?.expire_date,
      discountType: subscription?.couponDiscountType || couponDiscountTypeMap.percentage,
      couponUseCaseSign: subscription?.couponType,
    }));
};

/**
 * Convert partner referral coupon result to coupon state
 * Uses strategy factory pattern for type-specific conversion
 * @param {PreloadCouponBean} coupon - Partner referral coupon data
 * @returns {Object} Coupon state object
 */
export const convertPartnerReferCouponResult2State = (coupon = {}) => {
  return couponStrategyFactory.convertPartnerReferResultToState(coupon);
};

/**
 * Convert soft bundle info to state
 * @param {Object} bundleInfo - Soft bundle information
 * @param {Array} bundleInfo.bundlePlanPriceList - List of bundle plan prices
 * @param {Object} context - Vuex context object
 * @returns {Object} Soft bundle state object
 */
export const convertSoftBundleInfo2State = ({ bundlePlanPriceList = [] }, context = {}) => {
  // Transform bundle plan price list to discount plan list
  const discountPlanList = _.chain(bundlePlanPriceList)
    .map((item = {}) => {
      const { discountValue, discountType } = item;

      return {
        planCode: item.planCode,
        promotionType: PROMOTION_TYPE.softBundle,
        forever: true, // Default is forever, no expire date for soft bundle
        discount: discountValue,
        discountType: discountType ?? couponDiscountTypeMap.percentage, // Default is percentage
        couponDefType: null, // Default is null, no coupon definition type for soft bundle
        couponPeriod: null, // Default is null, no coupon period for soft bundle
        expireDate: null, // Default is null, no expire date for soft bundle
      };
    })
    .filter(Boolean)
    .value();

  const softBundlePromotionInfo = {
    discountPlanList,
    status: couponFetchStatus.idle,
    lastStatus: couponFetchStatus.idle,
    promotionType: PROMOTION_TYPE.softBundle,
    getBundlePlanMap: () => {
      // Get plan map from discount plan list
      // note discountPlanList is obtained from bundlePlanPriceList
      // even if the current plan not have discount, still need config in bundlePlanPriceList, for example: zoom phone us/ca not have discount
      const { getters } = context;
      const { groupByPlanCodePlanMap } = getters;

      return _.chain(discountPlanList)
      .reduce((pre, item) => {
        const { planCode } = item;
        const plan = groupByPlanCodePlanMap[planCode];
        return {
          ...pre,
          [planCode]: {
            ...plan,
            discountInfo: item,
          },
        };
      }, {})
      .value();
    },
    validate: async (options = {}) => {
      const { getters } = context;
      const { validateByReferralCoupon } = getters.partnerRefer?.rules || {};
      const { referralCouponCode } = getters.partnerRefer || {};
      const promotionType = PROMOTION_TYPE.softBundle;
      const { pureValidate = false, formatParams, currentSelectedPlans = getters.getSelectedPlans } = options;
      const shouldCommit = !pureValidate;

      const { pending, failure, successfully } = couponFetchStatus;

      // Skip validation if not required or no referral coupon code
      if (!validateByReferralCoupon || isEmptyVal(referralCouponCode)) {
        return Promise.resolve(getters.promotionInfo?.[promotionType]?.status || couponFetchStatus.idle);
      }

      if (!softBundlePromotionInfo.validateByPlanTypes(currentSelectedPlans)) {
        softBundlePromotionInfo.commitPromotionInfoStatus(shouldCommit, { promotionType, status: failure, lastStatus: failure });
        return Promise.resolve(failure);
      }

      try {
        const lastStatus = getters.promotionInfo?.[promotionType]?.lastStatus || couponFetchStatus.idle;
        softBundlePromotionInfo.commitPromotionInfoStatus(shouldCommit, { promotionType, status: pending, lastStatus: lastStatus });
        let params = couponParamsGeneratorInstance.generatePureValidateCouponParams({
          couponCode: referralCouponCode,
        });
        // custom format params
        if (_.isFunction(formatParams)) {
          params = formatParams(params);
        }
        await validateCoupon(params);
        // commit promotion info status
        softBundlePromotionInfo.commitPromotionInfoStatus(shouldCommit, { promotionType, status: successfully, lastStatus: successfully });
        return Promise.resolve(successfully);
      } catch (couponResult) {
        softBundlePromotionInfo.commitPromotionInfoStatus(shouldCommit, { promotionType, status: failure, lastStatus: failure });
        logger.error('validate soft bundle api', couponResult);
        return Promise.resolve(failure);
      }
    },
    validateByPlanTypes: (getSelectedPlans = []) => {
      const { getters } = context;
      const { feValidate = {} } = getters.softBundleConfiguration?.config || {};
      const { requiredPlanTypes = [] } = feValidate;
      const selectedPlanTypes = _.chain(getSelectedPlans)
        .groupBy('type')
        .keys()
        .value();

      if (requiredPlanTypes?.length) {
        return _.some(requiredPlanTypes, (type) => _.includes(selectedPlanTypes, type));
      }

      return true;
    },
    commitPromotionInfoStatus: (shouldCommit = true, payload = {}) => {
      const { commit } = context;
      shouldCommit && commit('updatePromotionInfoStatus', payload);
    },
    afterValidate() {
      const { commit, getters } = context;
      const { softBundleConfiguration = {}, softBundleState, couponState } = getters;
      const { config = {} } = softBundleConfiguration;
      const { showCouponFormWhenBundleInEligible } = config;

      // reset coupon state and pop messages
      if (showCouponFormWhenBundleInEligible && softBundleState.isSuccess && !!couponState.couponCode) {
        commit('resetCouponState');
        commit('setCouponFormMode', couponFormModeMap.INIT);
        softBundlePopUpMsgWithCoupon();
      }
    },
  };

  return softBundlePromotionInfo;
};

/**
 * Convert pure referral coupon info to state
 * @param {Object} context - Vuex context object
 * @returns {PromotionInfoBean} Pure referral coupon promotion info
 */
export const convertPureReferralCouponInfo2State = (context = {}) => {
  return {
    status: couponFetchStatus.idle,
    promotionType: PROMOTION_TYPE.pureReferralCoupon,
    validate: async () => {
      const { dispatch, getters, commit } = context;
      await dispatch('preCheckPartnerByReferralCoupon');
      const status = getters.referralCouponStatus;
      const promotionType = PROMOTION_TYPE.pureReferralCoupon;
      commit('updatePromotionInfoStatus', { promotionType, status });
      return Promise.resolve(status);
    },
  };
};

